import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import greyplaceholder from "../../assets/images/grey-person.jpg";
import * as constants from "../../utils/constants";
import { setUser } from "../../reducers/userSlice";
import { toast } from "react-toastify";
import { logout, updateUserProfile } from "../../api/FlaskAPI";
import "./UserProfile.css";
import {
  Button,
  TextField,
  Paper,
  Grid,
  Typography,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip as MuiChip,
} from "@mui/material";

export default function UserProfile() {
  const { t } = useTranslation();
  const logoutUserRequest = useApi(logout);
  const updateProfileRequest = useApi(updateUserProfile);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));

  const agriculturalFields = [
    "Agriculture",
    "Disaster Management",
    "Climate Monitoring",
    "Urban Planning",
    "Weather Forecasting",
    "Forest Mapping",
    "Water Management",
  ];
  const [editing, setEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({
    userId: Number(user?.id),
    first_name: user?.first_name || "",
    middle_name: user?.middle_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    organization: user?.organization || "",
    position: user?.position || "",
    fields: user?.fields || [],
  });

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
    errors: {},
  });

  const navigatePortal = () => {
    navigate("/admin");
  };

  const logoutUser = async () => {
    localStorage.removeItem(constants.USER);
    dispatch(setUser(""));
    await logoutUserRequest.request({}, false);
    window.location.reload();
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditedUser({
      userId: Number(user?.id),
      first_name: user?.first_name || "",
      middle_name: user?.middle_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      organization: user?.organization || "",
      position: user?.position || "",
      fields: user?.fields || [],
    });
    setShowPasswordChange(false);
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
      errors: {},
    });
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      let updateData = { ...editedUser };

      if (showPasswordChange) {
        const { currentPassword, newPassword, confirmNewPassword } =
          passwordData;

        // Validate passwords
        let passwordErrors = {};
        if (!currentPassword)
          passwordErrors.currentPassword = "Current password is required";
        if (!newPassword)
          passwordErrors.newPassword = "New password is required";
        if (!confirmNewPassword)
          passwordErrors.confirmNewPassword = "Please confirm new password";
        if (newPassword !== confirmNewPassword)
          passwordErrors.confirmNewPassword = "Passwords do not match";

        if (Object.keys(passwordErrors).length > 0) {
          setPasswordData({ ...passwordData, errors: passwordErrors });
          return;
        }

        updateData.currentPassword = currentPassword;
        updateData.newPassword = newPassword;
      }
      const response = await updateProfileRequest.request(updateData);
      if (response.status === 200) {
        // Update local storage with new user data
        const updatedUser = { ...user, ...editedUser };
        localStorage.setItem(constants.USER, JSON.stringify(updatedUser));
        dispatch(setUser(updatedUser));
        toast.success(t("Profile updated successfully"));
        setEditing(false);
        setShowPasswordChange(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
          errors: {},
        });
      }
    } catch (error) {
      toast.error(t("Failed to update profile"));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedUser({ ...editedUser, [name]: value });
  };

  return (
    <Paper elevation={3} className="profile-card-container">
      <Box className="profile-header">
        <div className="profile-image-wrapper">
          <img className="profile-image" src={greyplaceholder} alt="Profile" />
        </div>
        <Typography variant="h4" className="profile-name">
          {user?.first_name} {user?.middle_name} {user?.last_name}
        </Typography>
        <Chip
          label={
            user?.role === "1"
              ? "User"
              : user?.role === "2"
              ? t("Sign.country_admin")
              : t("Sign.super_admin")
          }
          color={
            user?.role === "1"
              ? "primary"
              : user?.role === "2"
              ? "secondary"
              : "error"
          }
          className="role-chip"
        />
      </Box>

      <Divider sx={{ my: 4 }} />

      <Grid
        container
        height={"450px"}
        overflow="scroll"
        spacing={{ xs: 2, sm: 3, md: 4 }}
        className="profile-content"
      >
        <Grid item xs={12} sm={12} md={6}>
          <Typography variant="h6" className="section-title" sx={{ mb: 3 }}>
            {t("Sign.personal_information")}
          </Typography>

          {editing ? (
            <>
              <TextField
                fullWidth
                label={t("Sign.first_name")}
                name="first_name"
                value={editedUser.first_name}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={t("Sign.middle_name")}
                name="middle_name"
                value={editedUser.middle_name}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={t("Sign.last_name")}
                name="last_name"
                value={editedUser.last_name}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
              />
              <TextField
                fullWidth
                label={t("Sign.email")}
                name="email"
                value={editedUser.email}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
              />
              {editedUser.phone && (
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={editedUser.phone}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                />
              )}
              <Button
                variant="outlined"
                onClick={() => setShowPasswordChange(!showPasswordChange)}
                sx={{ mt: 2, mb: 2 }}
              >
                {showPasswordChange
                  ? "Cancel Password Change"
                  : "Change Password"}
              </Button>

              {showPasswordChange && (
                <>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    margin="normal"
                    variant="outlined"
                    error={!!passwordData.errors.currentPassword}
                    helperText={passwordData.errors.currentPassword}
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value,
                      })
                    }
                    margin="normal"
                    variant="outlined"
                    error={!!passwordData.errors.newPassword}
                    helperText={passwordData.errors.newPassword}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmNewPassword"
                    type="password"
                    value={passwordData.confirmNewPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        confirmNewPassword: e.target.value,
                      })
                    }
                    margin="normal"
                    variant="outlined"
                    error={!!passwordData.errors.confirmNewPassword}
                    helperText={passwordData.errors.confirmNewPassword}
                  />
                </>
              )}
            </>
          ) : (
            <Box className="info-list">
              {user?.username && (
                <div className="info-item">
                  <Typography
                    variant="subtitle1"
                    className="info-label personal"
                  >
                    {t("Sign.username")}:
                  </Typography>
                  <Typography variant="body1" className="personal-info">
                    {user?.username}
                  </Typography>
                </div>
              )}
              {user?.first_name && (
                <div className="info-item">
                  <Typography
                    variant="subtitle1"
                    className="info-label personal"
                  >
                    {t("Sign.first_name")}:
                  </Typography>
                  <Typography variant="body1" className="personal-info">
                    {user?.first_name}
                  </Typography>
                </div>
              )}
              {user?.middle_name && (
                <div className="info-item">
                  <Typography
                    variant="subtitle1"
                    className="info-label personal"
                  >
                    {t("Sign.middle_name")}:
                  </Typography>
                  <Typography variant="body1" className="personal-info">
                    {user?.middle_name}
                  </Typography>
                </div>
              )}
              {user?.last_name && (
                <div className="info-item">
                  <Typography
                    variant="subtitle1"
                    className="info-label personal"
                  >
                    {t("Sign.last_name")}:
                  </Typography>
                  <Typography variant="body1" className="personal-info">
                    {user?.last_name}
                  </Typography>
                </div>
              )}
              {user?.email && (
                <div className="info-item">
                  <Typography variant="subtitle1" className="info-label">
                    {t("Sign.email")}:
                  </Typography>
                  <Typography variant="body1" className="personal-info">
                    {user?.email}
                  </Typography>
                </div>
              )}
              {user?.phone && (
                <div className="info-item">
                  <Typography variant="subtitle1" className="info-label">
                    {t("Sign.phone")}:
                  </Typography>
                  <Typography variant="body1" className="personal-info">
                    {user?.phone}
                  </Typography>
                </div>
              )}
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <Typography variant="h6" className="section-title">
            {t("Sign.professional_information")}
          </Typography>

          {editing ? (
            <>
              {user?.role === "1" && (
                <>
                  <TextField
                    fullWidth
                    label={t("Sign.organization")}
                    name="organization"
                    value={editedUser.organization}
                    onChange={handleChange}
                    margin="normal"
                    variant="outlined"
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t('Sign.field_of_expertise')}</InputLabel>
                    <Select
                      multiple
                      value={editedUser.fields}
                      onChange={(e) =>
                        setEditedUser({ ...editedUser, fields: e.target.value })
                      }
                      input={<OutlinedInput label="Fields of Expertise" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <MuiChip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {agriculturalFields.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
              {user?.role === "2" && (
                <>
                  <TextField
                    fullWidth
                    label="Position"
                    name="position"
                    value={editedUser.position}
                    onChange={handleChange}
                    margin="normal"
                    variant="outlined"
                  />
                </>
              )}
            </>
          ) : (
            <Box className="info-list">
              {user?.role === "2" && (
                <>
                  {user?.country && (
                    <div className="info-item">
                      <Typography variant="subtitle1" className="info-label">
                        {t("Sign.country")}:
                      </Typography>
                      <Typography variant="body1">{user?.country}</Typography>
                    </div>
                  )}
                  {user?.position && (
                    <div className="info-item">
                      <Typography variant="subtitle1" className="info-label">
                        {t("Sign.position")}:
                      </Typography>
                      <Typography variant="body1">{user?.position}</Typography>
                    </div>
                  )}
                </>
              )}
              {user?.role === "1" && (
                <>
                  {user?.organization && (
                    <div className="info-item">
                      <Typography variant="subtitle1" className="info-label">
                        {t("Sign.organization")}:
                      </Typography>
                      <Typography variant="body1">
                        {user?.organization}
                      </Typography>
                    </div>
                  )}
                </>
              )}
              {user?.role === "1" && user?.fields && (
                <>
                  {user?.fields && (
                    <div className="info-item">
                      <Typography variant="subtitle1" className="info-label">
                        {t("Sign.field_of_expertise")}:
                      </Typography>
                      <Box className="fields-tags">
                        {user?.fields.map((field, index) => (
                          <Chip
                            key={index}
                            label={field}
                            className="field-chip"
                          />
                        ))}
                      </Box>
                    </div>
                  )}
                </>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: { xs: 3, sm: 4 } }} />

      <Box className="profile-actions" sx={{ pb: { xs: 4, sm: 2 } }}>
        {editing ? (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              className="action-button"
              fullWidth={false}
              sx={{ mb: { xs: 2, sm: 0 } }}
            >
              {t('save_changes')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              sx={{
                ml: { xs: 0, sm: 2 },
                mt: { xs: 0, sm: 0 },
                mb: { xs: 2, sm: 0 },
              }}
              className="action-button"
              fullWidth={false}
            >
              {t('cancel')}
            </Button>
          </>
        ) : (
          <>
            {user?.role === "1" && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                className="action-button"
                fullWidth={false}
                sx={{ mb: { xs: 2, sm: 0 }, ml: { xs: 12, sm: 1 } }}
              >
                {t('edit_profile')}
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={logoutUser}
              sx={{
                ml: { xs: 0, sm: user?.role === "1" ? 2 : 0 },
                mb: { xs: 2, sm: 0 },
                gap: 1.5,
              }}
              className="action-button"
              fullWidth={false}
            >
              {t("Sign.logout")}
            </Button>
            {user?.role === "3" && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AdminPanelSettingsIcon />}
                onClick={navigatePortal}
                sx={{
                  ml: { xs: 0, sm: 2 },
                  mb: { xs: 2, sm: 0 },
                }}
                className="action-button"
                fullWidth={false}
              >
                {t("Sign.admin_panel")}
              </Button>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}
